import React from 'react';
import * as Icons from 'lucide-react';

export const LinkPreview = ({ component, onAction }) => {
  const { specificAttrs = {} } = component;
  const {
    linkType = 'button',
    label = 'Click Here',
    href = '',
    targetPage = '',
    openInNewTab = false,
    variant = 'primary',
    size = 'medium',
    icon = '',
    iconPosition = 'left',
    fullWidth = false,
    underline = false,
    textColor = '#3B82F6',
    backgroundColor = '#3B82F6',
    hoverColor = '#2563EB'
  } = specificAttrs;

  const IconComponent = icon && Icons[icon] ? Icons[icon] : null;

  // Handle click
  const handleClick = (e) => {
    if (onAction && typeof onAction === 'function') {
      e.preventDefault();
      onAction(component);
    }
  };

  // Button styles based on variant and size
  const getButtonStyle = () => {
    const sizeStyles = {
      small: { padding: '6px 12px', fontSize: '13px' },
      medium: { padding: '10px 20px', fontSize: '14px' },
      large: { padding: '14px 28px', fontSize: '16px' }
    };

    const baseStyle = {
      ...sizeStyles[size],
      borderRadius: '6px',
      border: 'none',
      cursor: 'pointer',
      fontWeight: '500',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s',
      textDecoration: 'none',
      width: fullWidth ? '100%' : 'auto',
      justifyContent: fullWidth ? 'center' : 'flex-start'
    };

    if (variant === 'primary') {
      return {
        ...baseStyle,
        backgroundColor: backgroundColor,
        color: '#FFFFFF',
        ':hover': {
          backgroundColor: hoverColor
        }
      };
    } else if (variant === 'secondary') {
      return {
        ...baseStyle,
        backgroundColor: 'transparent',
        color: textColor,
        border: `2px solid ${textColor}`,
        ':hover': {
          backgroundColor: `${textColor}15`
        }
      };
    } else if (variant === 'ghost') {
      return {
        ...baseStyle,
        backgroundColor: 'transparent',
        color: textColor,
        ':hover': {
          backgroundColor: `${textColor}10`
        }
      };
    } else if (variant === 'danger') {
      return {
        ...baseStyle,
        backgroundColor: '#DC2626',
        color: '#FFFFFF',
        ':hover': {
          backgroundColor: '#B91C1C'
        }
      };
    } else if (variant === 'success') {
      return {
        ...baseStyle,
        backgroundColor: '#10B981',
        color: '#FFFFFF',
        ':hover': {
          backgroundColor: '#059669'
        }
      };
    }

    return baseStyle;
  };

  // Text link style
  const getTextLinkStyle = () => {
    return {
      color: textColor,
      fontSize: size === 'small' ? '13px' : size === 'large' ? '16px' : '14px',
      textDecoration: underline ? 'underline' : 'none',
      cursor: 'pointer',
      transition: 'all 0.2s',
      ':hover': {
        textDecoration: 'underline'
      }
    };
  };

  // Icon button style
  const getIconButtonStyle = () => {
    const sizeMap = {
      small: { width: '32px', height: '32px', fontSize: '16px' },
      medium: { width: '40px', height: '40px', fontSize: '20px' },
      large: { width: '48px', height: '48px', fontSize: '24px' }
    };

    return {
      ...sizeMap[size],
      borderRadius: '50%',
      border: 'none',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: backgroundColor,
      color: '#FFFFFF',
      transition: 'all 0.2s',
      ':hover': {
        backgroundColor: hoverColor
      }
    };
  };

  // Card link style
  const getCardLinkStyle = () => {
    return {
      padding: '16px',
      borderRadius: '8px',
      border: `1px solid ${textColor}30`,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '12px',
      transition: 'all 0.2s',
      textDecoration: 'none',
      color: textColor,
      width: fullWidth ? '100%' : 'auto',
      ':hover': {
        borderColor: textColor,
        backgroundColor: `${textColor}05`
      }
    };
  };

  // Render icon
  const renderIcon = () => {
    if (!IconComponent) return null;
    const iconSize = size === 'small' ? 16 : size === 'large' ? 20 : 18;
    return <IconComponent size={iconSize} />;
  };

  // Render based on link type
  if (linkType === 'text-link') {
    return (
      <a
        href={href || '#'}
        target={openInNewTab ? '_blank' : '_self'}
        rel={openInNewTab ? 'noopener noreferrer' : undefined}
        onClick={handleClick}
        style={getTextLinkStyle()}
      >
        {label}
      </a>
    );
  }

  if (linkType === 'icon-button') {
    return (
      <a
        href={href || '#'}
        target={openInNewTab ? '_blank' : '_self'}
        rel={openInNewTab ? 'noopener noreferrer' : undefined}
        onClick={handleClick}
        style={getIconButtonStyle()}
        title={label}
      >
        {renderIcon()}
      </a>
    );
  }

  if (linkType === 'card-link') {
    return (
      <a
        href={href || '#'}
        target={openInNewTab ? '_blank' : '_self'}
        rel={openInNewTab ? 'noopener noreferrer' : undefined}
        onClick={handleClick}
        style={getCardLinkStyle()}
      >
        {IconComponent && iconPosition === 'left' && renderIcon()}
        <span style={{ flex: 1, fontSize: size === 'small' ? '13px' : size === 'large' ? '16px' : '14px' }}>
          {label}
        </span>
        {IconComponent && iconPosition === 'right' && renderIcon()}
      </a>
    );
  }

  // Default: button
  return (
    <a
      href={href || '#'}
      target={openInNewTab ? '_blank' : '_self'}
      rel={openInNewTab ? 'noopener noreferrer' : undefined}
      onClick={handleClick}
      style={getButtonStyle()}
    >
      {IconComponent && iconPosition === 'left' && renderIcon()}
      {label}
      {IconComponent && iconPosition === 'right' && renderIcon()}
    </a>
  );
};